import type { DropdownOption } from 'naive-ui';
import { DocumentState } from '@/api';
import { gaiaCheckedIcon } from '@/components/design/base/GaiaDropdown.vue';
import {
  LOCKED_DOCUMENT_STATES,
  documentStateLabels,
} from '@/components/design/domain/contractManagement/contractStateIcons';

/**
 * The "Set state" submenu every ⋯ that holds a contract file offers (contract
 * file row, contract file header), so moving a revision through its lifecycle
 * reads the same everywhere; plain vault files have no state to set. The tick
 * sits on the stored state. While the backend owns the state (processing,
 * sending or waiting for signature) nothing can be set, so the submenu is
 * disabled.
 */
const STATE_MENU_PREFIX = 'document-state:';

const SETTABLE_STATES = Object.values(DocumentState).filter(
  state => !LOCKED_DOCUMENT_STATES.includes(state)
);

export function documentStateMenuOption(current: DocumentState | null = null): DropdownOption {
  const locked = current !== null && LOCKED_DOCUMENT_STATES.includes(current);
  return {
    key: 'document-state',
    label: locked ? 'Set state (updating automatically)' : 'Set state',
    disabled: locked,
    children: SETTABLE_STATES.map(state => ({
      key: `${STATE_MENU_PREFIX}${state}`,
      label: documentStateLabels[state],
      icon: gaiaCheckedIcon(current === state),
    })),
  };
}

/** The state a submenu key asks for, or `undefined` when the key isn't one of the submenu's. */
export function documentStateFromMenuKey(key: string): DocumentState | undefined {
  return STATE_BY_KEY.get(key);
}

const STATE_BY_KEY = new Map<string, DocumentState>(
  SETTABLE_STATES.map(state => [`${STATE_MENU_PREFIX}${state}`, state] as const)
);
